import {
  notify,
  Popover,
  uniReactRoot,
  usePromptModal,
} from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { Menu, MenuItem } from '@affine/component/ui/menu';
import { PeekViewService } from '@affine/core/modules/peek-view';
import {
  demoFileMimeHint,
  demoSampleFile,
  isPreparedDemoSample,
  replacesDemoUserFiles,
  showDemoReplacementToast,
} from '@affine/core/modules/workspace/utils/demo-input-policy';
import {
  composeBlobNameFromRename,
  resolveBlobDisplayName,
  resolveBlobDownloadName,
  splitBlobNameForRename,
} from '@affine/core/utils/blob-display-name';
import {
  fileWithResolvedMimeType,
  resolveBlobMimeTypeHint,
  sniffBlobFileType,
} from '@affine/core/utils/blob-file-type';
import { I18n } from '@affine/i18n';
import {
  type Cell,
  type CellRenderProps,
  createIcon,
  type DatabaseAttachmentBlobReference,
  type DataViewCellLifeCycle,
  EditorHostKey,
  getClipboardFiles,
} from '@blocksuite/affine/blocks/database';
import { copyDatabaseFileCellIndexReviewSubject } from '@blocksuite/affine/shared/services';
import {
  isHeicMime,
  openFilesWith,
  stageBlob,
} from '@blocksuite/affine/shared/utils';
import type { BlobEngine } from '@blocksuite/affine/sync';
import {
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  FileIcon,
  MoreHorizontalIcon,
  PlusIcon,
} from '@blocksuite/icons/rc';
import type { BlockStdScope } from '@blocksuite/std';
import {
  computed,
  type ReadonlySignal,
  type Signal,
  signal,
} from '@preact/signals-core';
import {
  buildDatabaseCellAttachmentSourceKey,
  generateFractionalIndexingKeyBetween,
} from '@streamlain/database-identity';
import { downloadFile } from '@streamlain/workspace-editor/shared/resource';
import { useLiveData, useService } from '@toeverything/infra';
import type { FileTypeResult } from 'file-type';
import { createStore, del, get, set } from 'idb-keyval';
import { nanoid } from 'nanoid';
import type { ForwardRefRenderFunction, MouseEvent, ReactNode } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { UserQuotaService } from '../../../../modules/cloud/services/user-quota';
import { useSignalValue } from '../../../../modules/doc-info/utils';
import type { ImageData } from '../../../../modules/peek-view/view/image-preview';
import type { MediaData } from '../../../../modules/peek-view/view/media-preview';
import type { AttachmentViewerSource } from '../../../attachment-viewer/types';
import { CircularProgress } from '../../components/loading';
import { progressIconContainer } from '../../components/loading.css';
import type {
  FileCellJsonValueType,
  FileCellRawValueType,
  FileItemType,
} from './define';
import {
  filePropertyModelConfig,
  getFileCellItems,
  normalizeFileCellItems,
  renameFileCellItem,
  replaceFileCellItemBlob,
} from './define';
import * as styles from './style.css';

// ---------------------------------------------------------------------------
// Multi-file download: 1 file → direct, 2+ files → zip via fflate
// ---------------------------------------------------------------------------

async function downloadAllFiles(
  manager: FileCellManager,
  fileList: FileItemRenderType[]
) {
  if (fileList.length === 0) return;

  const doneFiles = fileList.filter(f => f.type === 'done');
  if (doneFiles.length === 0) return;

  // Single file → direct download
  if (doneFiles.length === 1) {
    const file = doneFiles[0];
    await downloadSingleDoneFile(manager, file);
    return;
  }

  // Multiple files → zip
  try {
    const { Zip, ZipPassThrough } = await import('fflate');
    const chunks: BlobPart[] = [];
    let resolveZip!: (blob: Blob) => void;
    let rejectZip!: (error: unknown) => void;
    const zipComplete = new Promise<Blob>((resolve, reject) => {
      resolveZip = resolve;
      rejectZip = reject;
    });
    const zip = new Zip((err, data, final) => {
      if (err) {
        rejectZip(err);
        return;
      }
      if (data) chunks.push(new Uint8Array(data));
      if (final) {
        resolveZip(new Blob(chunks, { type: 'application/zip' }));
      }
    });
    const usedNames = new Set<string>();
    let addedEntries = 0;

    try {
      for (const file of doneFiles) {
        const blob = await fetchFileCellBlob(manager, file.id);
        if (!blob) continue;
        let name = getFileDownloadName(file);
        // Deduplicate filenames
        if (usedNames.has(name)) {
          const dot = name.lastIndexOf('.');
          const base = dot > 0 ? name.slice(0, dot) : name;
          const ext = dot > 0 ? name.slice(dot) : '';
          let i = 2;
          while (usedNames.has(`${base}_${i}${ext}`)) i++;
          name = `${base}_${i}${ext}`;
        }
        usedNames.add(name);
        const zipEntry = new ZipPassThrough(name);
        zip.add(zipEntry);
        addedEntries++;
        await streamBlobToZipEntry(blob, zipEntry);
      }
    } catch (error) {
      zip.terminate();
      throw error;
    }

    if (addedEntries === 0) {
      zip.terminate();
      throw new Error('No downloadable attachments found');
    }
    zip.end();
    const zipBlob = await zipComplete;
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attachments-${doneFiles.length}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('[download] Zip creation failed:', err);
    notify.error({ title: 'Download failed' });
  }
}

async function downloadSingleDoneFile(
  manager: FileCellManager,
  file: FileItemDoneType
) {
  try {
    const blob = await fetchFileCellBlob(manager, file.id);
    if (!blob) {
      throw new Error('Attachment blob not found');
    }
    const url = URL.createObjectURL(blob);
    downloadFile(url, getFileDownloadName(file));
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    console.error('[download] Failed:', err);
    notify.error({ title: 'Download failed' });
  }
}

async function fetchFileCellBlob(
  manager: FileCellManager,
  blobId: string
): Promise<Blob | null> {
  return manager.getFileBlob(blobId);
}

async function streamBlobToZipEntry(
  blob: Blob,
  zipEntry: { push: (chunk: Uint8Array, final?: boolean) => void }
): Promise<void> {
  const reader = blob.stream().getReader();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        zipEntry.push(new Uint8Array(), true);
        return;
      }
      zipEntry.push(value, false);
    }
  } finally {
    reader.releaseLock();
  }
}

// ---------------------------------------------------------------------------
// Cross-cell attachment drag-and-drop (pointer-event based)
//
// HTML5 Drag is impossible inside wc-dnd: the MouseSession adds a global
// `dragstart → preventDefault` listener. Instead we use setPointerCapture
// which locks all pointer events to the capturing element, bypassing wc-dnd.
// ---------------------------------------------------------------------------

interface AttachmentDragPayload {
  sourceRowId: string;
  sourcePropertyId: string;
  sourceRefId: string;
  blobId: string;
  dataSourceRef: object;
}

let activeDrag: AttachmentDragPayload | null = null;

/** Minimum px movement before a pointer-drag activates. */
const DRAG_THRESHOLD = 5;

interface AttachmentDropTarget {
  dropElement: HTMLElement;
  highlightElement: HTMLElement;
}

/** Registry of mounted drop targets (file cells own drops; table cells own visual feedback). */
const dropTargetRegistry = new Set<AttachmentDropTarget>();

function getCellDropHighlightElement(el: HTMLElement): HTMLElement {
  return (
    el.closest<HTMLElement>('[data-database-cell-frame="true"]') ??
    el.closest<HTMLElement>(
      'dv-table-view-cell-container, affine-database-virtual-cell-container'
    ) ??
    el
  );
}

/** Find drop target by checking bounding rects (immune to Shadow DOM boundaries). */
function findDropTarget(x: number, y: number): AttachmentDropTarget | null {
  for (const target of dropTargetRegistry) {
    const { highlightElement } = target;
    if (!target.dropElement.isConnected || !highlightElement.isConnected) {
      continue;
    }
    const rect = highlightElement.getBoundingClientRect();
    if (
      x >= rect.left &&
      x <= rect.right &&
      y >= rect.top &&
      y <= rect.bottom
    ) {
      return target;
    }
  }
  return null;
}

/** Floating ghost element shown during pointer drag. */
let ghostEl: HTMLDivElement | null = null;

function showDragGhost(label: string) {
  ghostEl = document.createElement('div');
  Object.assign(ghostEl.style, {
    position: 'fixed',
    zIndex: '99999',
    padding: '2px 8px',
    fontSize: '12px',
    lineHeight: '20px',
    borderRadius: '4px',
    background: 'var(--streamlain-v2-layer-background-primary, #fff)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border, #e0e0e0)',
    boxShadow: '0 2px 8px rgba(0,0,0,.12)',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    opacity: '0.9',
    transform: 'translate(-50%, -120%)',
  });
  ghostEl.textContent = label;
  document.body.appendChild(ghostEl);
}

function moveDragGhost(x: number, y: number) {
  if (ghostEl) {
    ghostEl.style.left = `${x}px`;
    ghostEl.style.top = `${y}px`;
  }
}

function hideDragGhost() {
  ghostEl?.remove();
  ghostEl = null;
}

/** Currently highlighted drop target. */
let currentDropTarget: AttachmentDropTarget | null = null;

function highlightDropTarget(target: AttachmentDropTarget | null) {
  if (currentDropTarget === target) return;
  if (currentDropTarget)
    currentDropTarget.highlightElement.removeAttribute('data-file-drag-over');
  currentDropTarget = target;
  if (currentDropTarget)
    currentDropTarget.highlightElement.setAttribute('data-file-drag-over', '');
}

function clearDropHighlight() {
  highlightDropTarget(null);
}

function moveAttachment(
  dataSource: {
    cellValueGet(rowId: string, propertyId: string): unknown;
    cellValueChange(rowId: string, propertyId: string, value: unknown): void;
  },
  from: { rowId: string; propertyId: string },
  to: { rowId: string; propertyId: string },
  refId: string,
  blobId: string
): { sourceRefId: string; targetRefId: string } | null {
  const sourceValue = {
    ...((dataSource.cellValueGet(from.rowId, from.propertyId) as
      | FileCellRawValueType
      | undefined) ?? {}),
  };
  const sourceRefId =
    sourceValue[refId] != null
      ? refId
      : Object.entries(sourceValue).find(([entryRefId, item]) => {
          const itemBlobId = item.id ?? entryRefId;
          return itemBlobId === blobId;
        })?.[0];
  if (!sourceRefId) return null;

  const item = sourceValue[sourceRefId];
  if (!item) return null;

  const { [sourceRefId]: _, ...remaining } = sourceValue;
  dataSource.cellValueChange(from.rowId, from.propertyId, remaining);

  const targetValue = {
    ...((dataSource.cellValueGet(to.rowId, to.propertyId) as
      | FileCellRawValueType
      | undefined) ?? {}),
  };
  const lastOrder =
    Object.values(targetValue)
      .sort((a, b) => (a.order > b.order ? 1 : -1))
      .at(-1)?.order ?? null;
  const targetRefId = createAttachmentReferenceId(blobId, targetValue);
  dataSource.cellValueChange(to.rowId, to.propertyId, {
    ...targetValue,
    [targetRefId]: {
      ...item,
      id: blobId,
      order: generateFractionalIndexingKeyBetween(lastOrder, null),
    },
  });
  return { sourceRefId, targetRefId };
}

function createAttachmentReferenceId(
  blobId: string,
  value: FileCellRawValueType
): string {
  if (value[blobId] == null) {
    return blobId;
  }

  let refId = `${blobId}:${nanoid(8)}`;
  while (value[refId] != null) {
    refId = `${blobId}:${nanoid(8)}`;
  }
  return refId;
}

// ---------------------------------------------------------------------------

/**
 * sl-luoo: Two-tier cache for file preview data.
 *
 * Tier 1 — Window-level singleton Map (in-memory):
 *   Key: blobId → Value: Signal<FileLoadData | undefined>
 *   Survives HMR + cell remount. Instant access (0ms).
 *   Lost on page reload.
 *
 * Tier 2 — IndexedDB (persistent, HEIC only):
 *   Key: blobId → Value: { jpeg: ArrayBuffer, fileType }
 *   Survives page reload. ~5-10ms read.
 *   Only stores decoded HEIC→JPEG because that's the expensive decode (500-2000ms).
 *   Regular images are fast enough from nbstore IDB (~10-50ms).
 */

interface FileUploadProgress {
  name: string;
  progress: number;
}

interface FileLoadData {
  blob: Blob;
  url: string;
  fileType?: FileTypeResult;
}

// -- Tier 1: Window-level singleton (in-memory, session lifetime) --
const FILE_PREVIEW_CACHE_KEY = '__slFilePreviewCache';
const filePreviewCache: Map<string, Signal<FileLoadData | undefined>> = ((
  globalThis as Record<string, unknown>
)[FILE_PREVIEW_CACHE_KEY] as Map<string, Signal<FileLoadData | undefined>>) ??
((globalThis as Record<string, unknown>)[FILE_PREVIEW_CACHE_KEY] = new Map<
  string,
  Signal<FileLoadData | undefined>
>());

const FILE_UPLOAD_PROGRESS_KEY = '__slFileUploadProgress';
const fileUploadProgressMap: Map<string, Signal<FileUploadProgress>> = ((
  globalThis as Record<string, unknown>
)[FILE_UPLOAD_PROGRESS_KEY] as Map<string, Signal<FileUploadProgress>>) ??
((globalThis as Record<string, unknown>)[FILE_UPLOAD_PROGRESS_KEY] = new Map<
  string,
  Signal<FileUploadProgress>
>());

type PendingUploadSignal = Signal<Record<string, FileItemUploadingType>>;
const FILE_CELL_PENDING_UPLOADS_KEY = '__slFileCellPendingUploads';
const fileCellPendingUploads: Map<string, PendingUploadSignal> =
  ((globalThis as Record<string, unknown>)[
    FILE_CELL_PENDING_UPLOADS_KEY
  ] as Map<string, PendingUploadSignal>) ??
  ((globalThis as Record<string, unknown>)[FILE_CELL_PENDING_UPLOADS_KEY] =
    new Map<string, PendingUploadSignal>());

function getPendingUploadSignal(cellKey: string): PendingUploadSignal {
  const existing = fileCellPendingUploads.get(cellKey);
  if (existing) return existing;
  const created = signal<Record<string, FileItemUploadingType>>({});
  fileCellPendingUploads.set(cellKey, created);
  return created;
}

// -- Tier 2: IndexedDB persistent cache (HEIC decoded JPEGs only) --
const heicDecodeStore = createStore('sl-heic-decode-cache', 'decoded');

interface HeicDecodeRecord {
  jpeg: ArrayBuffer;
  mime: string; // original MIME (image/heic)
  ext: string; // original extension
  decodedAt: number; // timestamp for TTL
}

const HEIC_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Read decoded JPEG from persistent cache. Returns objectURL or null. */
async function heicCacheGet(
  blobId: string
): Promise<{ url: string; fileType: FileTypeResult } | null> {
  try {
    const record = await get<HeicDecodeRecord>(blobId, heicDecodeStore);
    if (!record) return null;
    if (Date.now() - record.decodedAt > HEIC_CACHE_TTL_MS) {
      // Expired — clean up asynchronously
      del(blobId, heicDecodeStore).catch(() => {});
      return null;
    }
    const blob = new Blob([record.jpeg], { type: 'image/jpeg' });
    return {
      url: URL.createObjectURL(blob),
      fileType: { ext: record.ext, mime: record.mime } as FileTypeResult,
    };
  } catch {
    return null;
  }
}

/** Store decoded JPEG in persistent cache. */
async function heicCacheSet(
  blobId: string,
  jpegBlob: Blob,
  fileType: FileTypeResult
): Promise<void> {
  try {
    await set(
      blobId,
      {
        jpeg: await jpegBlob.arrayBuffer(),
        mime: fileType.mime,
        ext: fileType.ext,
        decodedAt: Date.now(),
      } satisfies HeicDecodeRecord,
      heicDecodeStore
    );
  } catch {
    // IDB write failure is non-critical — in-memory cache still works
  }
}

class FileUploadManager {
  constructor(
    private readonly blobSync: BlobEngine,
    private readonly std: BlockStdScope | undefined
  ) {}

  uploadFile(
    file: File,
    onComplete: (
      result?: { blobId: string; mime: string; file: File },
      error?: unknown
    ) => void
  ): string {
    const tempId = nanoid();

    const progress = signal<FileUploadProgress>({
      progress: 0,
      name:
        I18n.language === 'de' ? 'Datei wird vorbereitet' : 'Preparing file',
    });
    fileUploadProgressMap.set(tempId, progress);
    this.startUpload(file, tempId)
      .then(result => {
        fileUploadProgressMap.delete(tempId);
        onComplete?.(result);
      })
      .catch(error => {
        fileUploadProgressMap.delete(tempId);
        console.error('[database-attachment] Local staging failed', error);
        onComplete(undefined, error);
      });
    return tempId;
  }

  async startUpload(
    file: File,
    fileId: string
  ): Promise<{ blobId: string; mime: string; file: File } | undefined> {
    let progress = fileUploadProgressMap.get(fileId);
    if (!progress) {
      return;
    }
    progress.value = {
      ...progress.value,
      progress: 5,
    };

    progress.value = {
      ...progress.value,
      progress: 25,
    };
    this.simulateUploadProgress(fileId);

    if (!this.std) {
      throw new Error('Database file upload requires editor std scope');
    }
    const workspaceId = this.std.store.workspace.id;
    const selectedFile = (await replacesDemoUserFiles(workspaceId))
      ? isPreparedDemoSample(file)
        ? file
        : await demoSampleFile(workspaceId, { mime: demoFileMimeHint(file) })
      : file;
    if (selectedFile !== file) showDemoReplacementToast();
    const uploadFile = await fileWithResolvedMimeType(selectedFile);
    const uploadedId = await stageBlob(
      this.std,
      uploadFile,
      (uploaded, total) => {
        const current = fileUploadProgressMap.get(fileId);
        if (!current) return;
        current.value = {
          ...current.value,
          progress: Math.min(92, 25 + (uploaded / Math.max(total, 1)) * 67),
        };
      }
    );
    progress = fileUploadProgressMap.get(fileId);
    if (!progress) {
      return;
    }
    progress.value = {
      ...progress.value,
      progress: 100,
    };

    return { blobId: uploadedId, mime: uploadFile.type, file: uploadFile };
  }

  getUploadProgress(
    fileId: string
  ): ReadonlySignal<FileUploadProgress> | undefined {
    return fileUploadProgressMap.get(fileId);
  }

  async getFileBlob(blobId: string): Promise<Blob | null> {
    return this.blobSync?.get(blobId);
  }

  /** Store a generated blob through the local-first streamable path. */
  async storeBlob(blob: Blob, filename = 'generated-blob'): Promise<string> {
    if (!this.std) {
      throw new Error('Database file upload requires editor std scope');
    }
    const file = new File([blob], filename, {
      type: blob.type || 'application/octet-stream',
    });
    return stageBlob(this.std, file);
  }

  /**
   * Get file preview data for a blobId. Returns a shared signal from the
   * window-level singleton cache. On first access the blob is fetched from
   * the workspace blob cache, MIME detected, and for HEIC decoded to JPEG. On subsequent
   * access (even from a different FileUploadManager instance after cell
   * remount) the same already-resolved signal is returned instantly.
   */
  getFileInfo(blobId: string): ReadonlySignal<FileLoadData | undefined> {
    // Singleton cache: same signal shared across all manager instances.
    const existing = filePreviewCache.get(blobId);
    if (existing) {
      return existing;
    }

    const fileLoadData = signal<FileLoadData | undefined>(undefined);
    filePreviewCache.set(blobId, fileLoadData);

    // Async path: fetch blob, detect MIME, decode HEIC if needed.
    // For HEIC: Tier 2 (IDB decoded cache) is checked before expensive heic-to.
    this._loadFilePreview(blobId, fileLoadData).catch(err => {
      console.error('[FileUploadManager] getFileInfo failed:', blobId, err);
    });
    return fileLoadData;
  }

  /**
   * Internal: load file preview data with two-tier HEIC caching.
   *
   * Flow for HEIC:
   *   1. Check Tier 2 (IDB decoded cache) → if hit, create objectURL, done (~5-10ms)
   *   2. Fetch raw blob from the workspace blob cache
   *   3. Detect MIME via fileTypeFromBuffer
   *   4. Decode via heic-to → store in Tier 2 for next session
   *
   * Flow for other images:
   *   1. Fetch raw blob from the workspace blob cache
   *   2. Detect MIME, create objectURL
   */
  private async _loadFilePreview(
    blobId: string,
    fileLoadData: Signal<FileLoadData | undefined>
  ): Promise<void> {
    // Tier 2: Check persistent IDB cache for previously decoded HEIC.
    // This is async but much faster (~5ms) than a full heic-to decode (~500-2000ms).
    const cached = await heicCacheGet(blobId);
    if (cached) {
      fileLoadData.value = {
        blob: new Blob(), // placeholder — download uses getFileBlob() directly
        url: cached.url,
        fileType: cached.fileType,
      };
      return;
    }

    // Fetch raw blob through blobSync; cloud workspaces resolve OPFS first.
    const blob = await this.getFileBlob(blobId);
    if (!blob) return;

    const fileType = await sniffBlobFileType(blob);
    const mime = fileType?.mime ?? blob.type;

    // HEIC/HEIF: decode to JPEG via heic-to (WASM), persist decoded result
    if (mime === 'image/heic' || mime === 'image/heif') {
      const { heicDecode } = await import('@blocksuite/affine-shared/utils');
      const { jpeg: displayBlob } = await heicDecode(blob);
      const url = URL.createObjectURL(displayBlob);
      fileLoadData.value = { blob, url, fileType };
      // Persist decoded JPEG in Tier 2 (async, non-blocking)
      if (fileType) {
        heicCacheSet(blobId, displayBlob, fileType).catch(() => {});
      }
      return;
    }

    fileLoadData.value = {
      blob,
      url: URL.createObjectURL(blob),
      fileType,
    };
  }

  /**
   * Evict a blobId from the singleton preview cache. Call this after a blob
   * is replaced (e.g. HEIC→JPEG conversion) so the next access fetches the
   * new content.
   */
  static evictPreview(blobId: string): void {
    const entry = filePreviewCache.get(blobId);
    if (entry?.value?.url) {
      URL.revokeObjectURL(entry.value.url);
    }
    filePreviewCache.delete(blobId);
    // Also evict from persistent HEIC decode cache
    del(blobId, heicDecodeStore).catch(() => {});
  }

  private simulateUploadProgress(fileId: string): void {
    setTimeout(() => {
      const progress = fileUploadProgressMap.get(fileId);
      if (!progress || progress.value.progress >= 92) return;
      const next =
        progress.value.progress + (92 - progress.value.progress) / 12;
      progress.value = {
        ...progress.value,
        progress: Math.min(next, 92),
      };
      this.simulateUploadProgress(fileId);
    }, 250);
  }

  dispose(): void {
    // Upload progress is global so doc switches/remounts keep visible state.
    // Preview signals live in the singleton cache — do NOT dispose them.
  }
}

type FileItemDoneType = FileItemType & {
  type: 'done';
};
type FileItemUploadingType = {
  id: string;
  type: 'uploading';
  name: string;
  order: string;
};
type FileItemRenderType = FileItemDoneType | FileItemUploadingType;

function getFileDisplayName(file: FileItemRenderType): string {
  return resolveBlobDisplayName({
    blobId: file.id,
    occurrenceName: file.name,
    mimeType: file.type === 'done' ? file.mime : undefined,
  });
}

function getFileDownloadName(file: FileItemDoneType): string {
  return resolveBlobDownloadName({
    blobId: file.id,
    occurrenceName: file.name,
    mimeType: file.mime,
  });
}

function getFileDisplayNameWithoutExtension(file: FileItemRenderType): string {
  return getFileDisplayName(file).replace(/\.[^.]+$/u, '');
}

class FileCellManager {
  private readonly cell: Cell<FileCellRawValueType, FileCellJsonValueType, {}>;
  readonly selectCurrentCell: (editing: boolean) => void;
  private readonly blobSync?: BlobEngine;
  private readonly std?: BlockStdScope;
  private readonly docId?: string;
  private readonly workspaceId?: string;
  private readonly uploadingFiles: PendingUploadSignal;
  readonly isEditing: ReadonlySignal<boolean>;
  readonly fileUploadManager: FileUploadManager | undefined;
  doneFiles = computed(() => this.cell.value$.value ?? {});

  get readonly() {
    return this.cell.property.readonly$;
  }

  get rowId() {
    return this.cell.rowId;
  }

  get propertyId() {
    return this.cell.propertyId;
  }

  get dataSource() {
    return this.cell.view.manager.dataSource;
  }

  get databaseId() {
    const dataSource = this.dataSource as {
      blockId?: unknown;
      databaseId?: unknown;
    };
    if (typeof dataSource.blockId === 'string' && dataSource.blockId) {
      return dataSource.blockId;
    }
    return typeof dataSource.databaseId === 'string'
      ? dataSource.databaseId
      : '';
  }

  get importWorkspaceId() {
    return this.workspaceId;
  }

  constructor(
    props: CellRenderProps<{}, FileCellRawValueType, FileCellJsonValueType>,
    private readonly peekViewService: PeekViewService
  ) {
    this.cell = props.cell;
    this.selectCurrentCell = props.selectCurrentCell;
    this.isEditing = props.isEditing$;
    const editorHost = this.cell?.view?.serviceGet
      ? this.cell.view.serviceGet(EditorHostKey)
      : undefined;
    const store = editorHost?.store;
    const std = editorHost?.std;
    const dataSource = this.dataSource as {
      docId?: unknown;
      workspaceId?: unknown;
    };
    this.blobSync = store?.blobSync;
    this.std = std;
    this.docId =
      store?.id ??
      (typeof dataSource.docId === 'string' ? dataSource.docId : undefined);
    this.workspaceId =
      store?.workspace.id ??
      (typeof dataSource.workspaceId === 'string'
        ? dataSource.workspaceId
        : undefined);
    this.uploadingFiles = getPendingUploadSignal(
      `${this.workspaceId ?? store?.id ?? 'unknown'}:${this.rowId}:${this.propertyId}`
    );

    this.fileUploadManager = this.blobSync
      ? new FileUploadManager(this.blobSync, std)
      : undefined;
  }

  dispose(): void {
    this.fileUploadManager?.dispose();
  }

  copyMovedAttachmentReviewMetadata(input: {
    sourceRowId: string;
    sourcePropertyId: string;
    sourceRefId: string;
    targetRowId: string;
    targetPropertyId: string;
    targetRefId: string;
    blobId: string;
  }): void {
    if (!this.std || !this.databaseId) return;
    copyDatabaseFileCellIndexReviewSubject({
      std: this.std,
      databaseId: this.databaseId,
      sourceRowId: input.sourceRowId,
      sourcePropertyId: input.sourcePropertyId,
      sourceRefId: input.sourceRefId,
      targetRowId: input.targetRowId,
      targetPropertyId: input.targetPropertyId,
      targetRefId: input.targetRefId,
      blobId: input.blobId,
    });
  }

  async getFileBlob(blobId: string): Promise<Blob | null> {
    return (await this.fileUploadManager?.getFileBlob(blobId)) ?? null;
  }

  /**
   * sl-luoo.4: Replace a blob in the cell (e.g. HEIC → converted JPEG).
   * Removes the old entry and inserts the new one with updated metadata,
   * preserving the original sort order.
   */
  replaceBlob(
    referenceId: string,
    newBlobKey: string,
    newMime: string,
    newExtension: string,
    newSize?: number
  ): void {
    const value = normalizeFileCellItems(this.cell.value$.value);
    const resolvedRefId =
      value[referenceId] != null
        ? referenceId
        : Object.entries(value).find(([entryRefId, item]) => {
            const itemBlobId = item.id ?? entryRefId;
            return itemBlobId === referenceId;
          })?.[0];
    if (!resolvedRefId) return;

    const oldEntry = value[resolvedRefId];
    if (!oldEntry) return;

    // Evict old preview so next access fetches the new blob.
    FileUploadManager.evictPreview(oldEntry.id ?? resolvedRefId);

    // Replace extension in filename (e.g. photo.heic → photo.jpeg)
    const dotIdx = oldEntry.name.lastIndexOf('.');
    const baseName =
      dotIdx > 0 ? oldEntry.name.slice(0, dotIdx) : oldEntry.name;
    const newName = `${baseName}.${newExtension}`;

    const nextValue = replaceFileCellItemBlob(value, resolvedRefId, {
      blobId: newBlobKey,
      mimeType: newMime,
      size: newSize ?? oldEntry.size ?? 0,
      name: newName,
    });
    if (nextValue) {
      this.cell.valueSet(nextValue);
    }
  }

  removeFile = (file: FileItemRenderType, e?: MouseEvent): void => {
    e?.stopPropagation();

    if (file.type === 'uploading') {
      const newTemp = { ...this.uploadingFiles.value };
      delete newTemp[file.id];
      this.uploadingFiles.value = newTemp;
      return;
    }

    const value = normalizeFileCellItems(this.cell.value$.value);
    delete value[file.refId];
    this.cell.valueSet(value);
  };

  renameFile = (file: FileItemDoneType, nextName: string): void => {
    if (this.readonly.value) {
      return;
    }

    const nextValue = renameFileCellItem(
      normalizeFileCellItems(this.cell.value$.value),
      file.refId,
      nextName
    );
    if (!nextValue) {
      return;
    }
    this.cell.valueSet(nextValue);
  };

  uploadFile = (file: File): void => {
    if (!this.fileUploadManager) {
      return;
    }
    const lastFile = this.fileList.value[this.fileList.value.length - 1];
    const order = generateFractionalIndexingKeyBetween(
      lastFile?.order || null,
      null
    );

    const fileId = this.fileUploadManager.uploadFile(file, (result, error) => {
      if (result) {
        const value = { ...this.cell.value$.value };
        const refId = createAttachmentReferenceId(result.blobId, value);
        this.cell.valueSet({
          ...value,
          [refId]: {
            name: result.file.name,
            id: result.blobId,
            order,
            mime: result.mime,
            size: result.file.size,
          },
        });
      } else if (error) {
        notify.error({
          title: I18n.t('com.streamlain.database.attachment.uploadFailed'),
        });
      }
      this.removeFile(tempFile);
    });
    const tempFile: FileItemUploadingType = {
      id: fileId,
      type: 'uploading',
      name:
        I18n.language === 'de' ? 'Datei wird vorbereitet' : 'Preparing file',
      order,
    };
    this.uploadingFiles.value = {
      ...this.uploadingFiles.value,
      [fileId]: tempFile,
    };
  };

  addBlobReference = (blob: DatabaseAttachmentBlobReference): void => {
    const value = { ...this.cell.value$.value };
    const lastOrder =
      Object.values(value)
        .sort((a, b) => (a.order > b.order ? 1 : -1))
        .at(-1)?.order ?? null;
    const refId = createAttachmentReferenceId(blob.blobId, value);
    this.cell.valueSet({
      ...value,
      [refId]: {
        name: blob.name,
        id: blob.blobId,
        order: generateFractionalIndexingKeyBetween(lastOrder, null),
        mime: blob.mime,
        size: blob.size,
      },
    });
  };

  fileList = computed(() => {
    const uploadingList = Object.values(this.uploadingFiles.value);
    const doneList = getFileCellItems(
      this.doneFiles.value
    ).map<FileItemDoneType>(file => ({
      ...file,
      type: 'done',
    }));
    return [...doneList, ...uploadingList].sort((a, b) =>
      a.order > b.order ? 1 : -1
    );
  });

  handlePaste = (e: ClipboardEvent): void => {
    if (!this.fileUploadManager) return;
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const pastedFiles = getClipboardFiles(clipboardData);
    if (pastedFiles.length === 0) return;

    e.preventDefault();
    e.stopPropagation();
    for (const file of pastedFiles) {
      this.uploadFile(file);
    }
  };

  openPreview = (refId: string) => {
    const previewList = this.fileList.value
      .filter(v => v.type === 'done')
      .map(v => {
        const fileInfo = this.fileUploadManager?.getFileInfo(v.id).value;
        return {
          ...v,
          ...fileInfo,
          url: fileInfo?.url,
          previewMime: fileInfo?.fileType?.mime ?? v.mime,
        };
      });

    const currentFile = previewList.find(v => v.refId === refId);
    if (!currentFile) return;

    const currentMime =
      resolveBlobMimeTypeHint(
        currentFile.previewMime,
        getFileDisplayName(currentFile)
      ) ?? '';
    if (isPdfMime(currentMime) || isTextMime(currentMime)) {
      if (!this.docId || !this.databaseId) return;
      const canRename = !this.readonly.value;
      const source: AttachmentViewerSource = {
        kind: 'database-cell',
        id: buildDatabaseCellAttachmentSourceKey({
          databaseId: this.databaseId,
          rowId: this.rowId,
          columnId: this.propertyId,
          refId: currentFile.refId,
          blobId: currentFile.id,
        }),
        docId: this.docId,
        workspaceId: this.workspaceId,
        blobId: currentFile.id,
        name: getFileDisplayName(currentFile),
        mimeType: currentMime,
        size: currentFile.size ?? null,
        databaseAttachmentRefId: currentFile.refId,
        canRename,
        rename: canRename
          ? nextName => {
              this.renameFile(currentFile, nextName);
            }
          : undefined,
        canReplaceBlob: canRename,
        replaceBlob: canRename
          ? replacement => {
              const extension =
                splitBlobNameForRename(currentFile.name).extension.replace(
                  /^\./u,
                  ''
                ) || 'txt';
              this.replaceBlob(
                currentFile.refId,
                replacement.blobId,
                replacement.mimeType,
                extension,
                replacement.size
              );
            }
          : undefined,
        supportsBlockAnnotations: false,
        supportsPdfSearchIndex: isPdfMime(currentMime),
      };
      window.dispatchEvent(
        new CustomEvent('streamlain:attachment-fullscreen', {
          detail: { source },
        })
      );
      return;
    }

    if (SUPPORTED_IMAGE_MIME_TYPES.has(currentMime)) {
      const imageList = previewList.filter(v =>
        SUPPORTED_IMAGE_MIME_TYPES.has(v.previewMime ?? '')
      );
      const getImageData = (index: number): ImageData | undefined => {
        const file = imageList[index];
        if (!file) return;
        const previousIndex = index - 1;
        const nextIndex = index + 1;
        const hasPrevious = previousIndex >= 0;
        const hasNext = nextIndex < imageList.length;
        return {
          index,
          url: file.url ?? '',
          caption: getFileDisplayName(file),
          previous: hasPrevious ? () => getImageData(previousIndex) : undefined,
          next: hasNext ? () => getImageData(nextIndex) : undefined,
        };
      };
      const currentIndex = imageList.findIndex(v => v.refId === refId);
      if (currentIndex === -1) return;
      const imageData = getImageData(currentIndex);
      if (!imageData) return;
      this.peekViewService.peekView
        .open({
          type: 'image-list',
          data: {
            image: imageData,
            total: imageList.length,
          },
        })
        .catch(error => {
          console.error('Failed to open image list', error);
        });
      return;
    }

    if (isMediaMime(currentMime)) {
      const mediaList = previewList.filter(v => isMediaMime(v.previewMime));
      const getMediaData = (index: number): MediaData | undefined => {
        const file = mediaList[index];
        if (!file?.url) return;
        const previousIndex = index - 1;
        const nextIndex = index + 1;
        const hasPrevious = previousIndex >= 0;
        const hasNext = nextIndex < mediaList.length;
        const mime = file.previewMime ?? '';
        return {
          index,
          url: file.url,
          caption: getFileDisplayName(file),
          mime,
          kind: mime.startsWith('video/') ? 'video' : 'audio',
          previous: hasPrevious ? () => getMediaData(previousIndex) : undefined,
          next: hasNext ? () => getMediaData(nextIndex) : undefined,
        };
      };
      const currentIndex = mediaList.findIndex(v => v.refId === refId);
      if (currentIndex === -1) return;
      const mediaData = getMediaData(currentIndex);
      if (!mediaData) return;
      this.peekViewService.peekView
        .open({
          type: 'media-list',
          data: {
            media: mediaData,
            total: mediaList.length,
          },
        })
        .catch(error => {
          console.error('Failed to open media list', error);
        });
    }
  };
}

const SUPPORTED_IMAGE_MIME_TYPES = new Set([
  'image/apng',
  'image/avif',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp',
  'image/tiff',
  'image/bmp',
  'image/vnd.microsoft.icon',
]);

function isMediaMime(mime: string | undefined): boolean {
  return !!mime && (mime.startsWith('audio/') || mime.startsWith('video/'));
}

function isPdfMime(mime: string | undefined): boolean {
  return mime === 'application/pdf' || mime?.endsWith('/pdf') === true;
}

function isTextMime(mime: string | undefined): boolean {
  return mime?.startsWith('text/') === true;
}

const useFilePreview = (
  file: FileItemRenderType,
  manager: FileCellManager
): {
  preview: ReactNode;
  onPreview?: () => void;
  fileType:
    | 'uploading'
    | 'loading'
    | 'image'
    | 'media'
    | 'pdf'
    | 'text'
    | 'file';
} => {
  const fileUploadManager = manager.fileUploadManager;
  const uploadProgress = useSignalValue(
    file.type === 'uploading'
      ? fileUploadManager?.getUploadProgress(file.id)
      : undefined
  );
  const loadFileData = useSignalValue(
    file.type === 'done' ? fileUploadManager?.getFileInfo(file.id) : undefined
  );
  if (uploadProgress != null) {
    return {
      preview: (
        <div className={styles.uploadingPreviewContainer}>
          <div className={progressIconContainer}>
            <CircularProgress progress={uploadProgress.progress} />
          </div>
          <span className={styles.uploadingFileName}>
            {uploadProgress.name}
          </span>
          <span className={styles.uploadingProgressText}>
            {Math.floor(uploadProgress.progress)}%
          </span>
        </div>
      ),
      fileType: 'uploading',
    };
  }
  if (file.type === 'uploading') {
    return {
      preview: (
        <div className={styles.uploadingPreviewContainer}>
          <div className={progressIconContainer}>
            <CircularProgress progress={0} />
          </div>
          <span className={styles.uploadingFileName}>{file.name}</span>
        </div>
      ),
      fileType: 'uploading',
    };
  }
  const mime = resolveBlobMimeTypeHint(
    loadFileData?.fileType?.mime ??
      (file.type === 'done' ? file.mime : undefined),
    getFileDisplayName(file)
  );
  if (mime && SUPPORTED_IMAGE_MIME_TYPES.has(mime)) {
    if (loadFileData == null) {
      return {
        preview: null,
        fileType: 'loading',
      };
    }
    const onPreview = () => {
      manager.openPreview(file.refId);
    };
    return {
      onPreview,
      preview: (
        <img
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            onPreview();
          }}
          draggable={false}
          className={styles.imagePreviewIcon}
          src={loadFileData.url}
          alt={getFileDisplayName(file)}
        />
      ),
      fileType: 'image',
    };
  }

  if (isMediaMime(mime)) {
    const onPreview =
      loadFileData == null
        ? undefined
        : () => {
            manager.openPreview(file.refId);
          };
    return {
      onPreview,
      preview: (
        <FileIcon className={styles.iconPrimary} width={18} height={18} />
      ),
      fileType: 'media',
    };
  }

  if (isPdfMime(mime)) {
    return {
      onPreview: () => {
        manager.openPreview(file.refId);
      },
      preview: (
        <FileIcon className={styles.iconPrimary} width={18} height={18} />
      ),
      fileType: 'pdf',
    };
  }

  if (isTextMime(mime)) {
    return {
      onPreview: () => {
        manager.openPreview(file.refId);
      },
      preview: (
        <FileIcon className={styles.iconPrimary} width={18} height={18} />
      ),
      fileType: 'text',
    };
  }

  return {
    preview: <FileIcon className={styles.iconPrimary} width={18} height={18} />,
    fileType: 'file',
  };
};

const FilePreview = (props: {
  file: FileItemRenderType;
  manager: FileCellManager;
}) => {
  const { file, manager } = props;
  const { preview, fileType, onPreview } = useFilePreview(file, manager);
  const displayName = getFileDisplayName(file);
  if (fileType === 'file') {
    return <div className={styles.filePreviewContainer}>{displayName}</div>;
  }
  if (fileType === 'media' || fileType === 'pdf' || fileType === 'text') {
    return (
      <div
        className={styles.filePreviewContainer}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          onPreview?.();
        }}
        style={onPreview ? { cursor: 'pointer' } : undefined}
      >
        {displayName}
      </div>
    );
  }
  if (fileType === 'image') {
    const label = formatLabel(file);
    return (
      <div
        className={styles.imagePreviewContainer}
        style={{ cursor: 'pointer' }}
      >
        {label && <span className={styles.formatChip}>{label}</span>}
        <div
          style={{
            overflow: 'hidden',
            borderRadius: label ? '0 2px 2px 0' : '2px',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {preview}
        </div>
      </div>
    );
  }
  return preview;
};

/**
 * Draggable file item using pointer events + setPointerCapture.
 * This bypasses wc-dnd completely: pointer capture locks all subsequent
 * pointer events to the capturing element, so wc-dnd's container listener
 * never sees them.
 */
const DraggableFileItem = ({
  file,
  readonly,
  manager,
  onPointerDragStart,
}: {
  file: FileItemRenderType;
  readonly: boolean;
  manager: FileCellManager;
  onPointerDragStart: (file: FileItemRenderType) => void;
}) => {
  const isDraggable = file.type === 'done' && !readonly;
  const ref = useRef<HTMLDivElement>(null);
  const dragState = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    activated: boolean;
  } | null>(null);
  const suppressClick = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !isDraggable) return;

    const onPointerDown = (e: PointerEvent) => {
      // Only primary button
      if (e.button !== 0) return;
      dragState.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        activated: false,
      };
      // Don't capture yet — wait for threshold
    };

    const onPointerMove = (e: PointerEvent) => {
      const state = dragState.current;
      if (!state || state.pointerId !== e.pointerId) return;

      if (!state.activated) {
        const dx = e.clientX - state.startX;
        const dy = e.clientY - state.startY;
        if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;

        // Activate drag
        state.activated = true;
        suppressClick.current = true;
        e.preventDefault();
        e.stopPropagation();
        el.setPointerCapture(e.pointerId);
        onPointerDragStart(file);
        showDragGhost(getFileDisplayName(file));
      }

      moveDragGhost(e.clientX, e.clientY);

      // Find drop target via bounding rect registry (no elementFromPoint needed)
      const target = findDropTarget(e.clientX, e.clientY);
      highlightDropTarget(target);
    };

    const onPointerUp = (e: PointerEvent) => {
      const state = dragState.current;
      if (!state || state.pointerId !== e.pointerId) return;
      dragState.current = null;

      if (state.activated) {
        el.releasePointerCapture(e.pointerId);
        hideDragGhost();

        // Use the last tracked drop target (from pointermove)
        const target = currentDropTarget;
        clearDropHighlight();

        if (target) {
          target.dropElement.dispatchEvent(
            new CustomEvent('attachment-pointer-drop', {
              bubbles: false,
              detail: { blobId: activeDrag?.blobId },
            })
          );
        }
        activeDrag = null;
      }
    };

    const onPointerCancel = (e: PointerEvent) => {
      const state = dragState.current;
      if (!state || state.pointerId !== e.pointerId) return;
      dragState.current = null;
      hideDragGhost();
      clearDropHighlight();
      activeDrag = null;
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerCancel);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerCancel);
    };
  }, [isDraggable, file, onPointerDragStart]);

  return (
    <div
      ref={ref}
      className={styles.fileItemCell}
      style={isDraggable ? { cursor: 'grab' } : undefined}
      onClick={e => {
        if (suppressClick.current) {
          suppressClick.current = false;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.stopPropagation();
        manager.selectCurrentCell(true);
      }}
    >
      <FilePreview file={file} manager={manager} />
    </div>
  );
};

const FileCellComponent: ForwardRefRenderFunction<
  DataViewCellLifeCycle,
  CellRenderProps<{}, FileCellRawValueType, FileCellJsonValueType>
> = (props, ref): ReactNode => {
  const peekView = useService(PeekViewService);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const manager = useMemo(() => new FileCellManager(props, peekView), []);

  useEffect(() => {
    return () => {
      manager.dispose();
    };
  }, [manager]);

  useImperativeHandle(
    ref,
    () => ({
      beforeEnterEditMode: () => {
        return true;
      },
      beforeExitEditingMode: () => {},
      afterEnterEditingMode: () => {},
      focusCell: () => true,
      blurCell: () => true,
      forceUpdate: () => {},
    }),
    []
  );
  const fileList = useSignalValue(manager.fileList);
  const isEditing = useSignalValue(manager.isEditing);
  const quotaService = useService(UserQuotaService);
  const blobLimitLabel$ = useMemo(
    () => quotaService.quota.quota$.map(q => q?.humanReadable?.blobLimit),
    [quotaService]
  );
  const blobLimitLabel = useLiveData(blobLimitLabel$);
  useEffect(() => {
    if (!quotaService.quota.quota$.value) {
      quotaService.quota.revalidate();
    }
  }, [quotaService]);

  // -- Cross-cell attachment DnD (pointer-event based) --
  const readonly = useSignalValue(manager.readonly);
  const dropRef = useRef<HTMLDivElement>(null);

  // -- sl-luoo: Listen for window-level file-cell events dispatched from the
  // native database row context menu (popRowMenu in menu.ts). Each file cell
  // checks if it's a target via rowId:propertyId matching before acting.
  // This avoids DOM querySelector issues with virtual tables or off-screen cells.
  const cellId = `${manager.rowId}:${manager.propertyId}`;

  useEffect(() => {
    const isMyCellTargeted = (e: Event): boolean => {
      const cells = (e as CustomEvent).detail?.cells as
        | { rowId: string; propertyId: string }[]
        | undefined;
      return cells?.some(c => `${c.rowId}:${c.propertyId}` === cellId) ?? false;
    };

    const handleConvertHeic = async (e: Event) => {
      if (!isMyCellTargeted(e)) return;
      const fileUploadManager = manager.fileUploadManager;
      const heicFiles = fileList.filter(isConvertibleHeic);
      if (!fileUploadManager || heicFiles.length === 0) return;
      try {
        const { heicDecode } = await import('@blocksuite/affine-shared/utils');
        for (const file of heicFiles) {
          const blob = await fileUploadManager.getFileBlob(file.id);
          if (!blob) continue;
          const { jpeg } = await heicDecode(blob);
          const jpegKey = await fileUploadManager.storeBlob(
            jpeg,
            `${getFileDisplayNameWithoutExtension(file)}.jpg`
          );
          manager.replaceBlob(file.refId, jpegKey, 'image/jpeg', 'jpg');
        }
      } catch (err) {
        console.error('[sl-luoo] HEIC convert via context menu failed:', err);
      }
    };

    const handleDownloadAll = async (e: Event) => {
      if (!isMyCellTargeted(e)) return;
      await downloadAllFiles(manager, fileList);
    };

    const onConvertHeic = (e: Event) => {
      void handleConvertHeic(e);
    };
    const onDownloadAll = (e: Event) => {
      void handleDownloadAll(e);
    };
    window.addEventListener('sl-convert-heic', onConvertHeic);
    window.addEventListener('sl-download-all', onDownloadAll);
    return () => {
      window.removeEventListener('sl-convert-heic', onConvertHeic);
      window.removeEventListener('sl-download-all', onDownloadAll);
    };
  }, [cellId, fileList, manager]);

  // -- Clipboard paste support for images (focus + edit mode) --
  useEffect(() => {
    const container = dropRef.current?.closest(
      'dv-table-view-cell-container, affine-database-virtual-cell-container'
    );
    if (!container) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | {
            files?: File[];
            blobs?: DatabaseAttachmentBlobReference[];
          }
        | undefined;
      const files = detail?.files;
      if (files && manager.fileUploadManager) {
        for (const file of files) {
          manager.uploadFile(file);
        }
      }
      const blobs = detail?.blobs;
      if (blobs) {
        for (const blob of blobs) {
          manager.addBlobReference(blob);
        }
      }
    };
    container.addEventListener('database-paste-files', handler);
    return () => container.removeEventListener('database-paste-files', handler);
  }, [manager]);

  const handlePointerDragStart = useCallback(
    (file: FileItemRenderType) => {
      if (file.type !== 'done') return;
      activeDrag = {
        sourceRowId: manager.rowId,
        sourcePropertyId: manager.propertyId,
        sourceRefId: file.refId,
        blobId: file.id,
        dataSourceRef: manager.dataSource,
      };
    },
    [manager]
  );

  const openUploadDialog = useCallback(() => {
    if (readonly || !manager.fileUploadManager) return;
    openFilesWith('Any', true, { workspaceId: manager.importWorkspaceId })
      .then(files => {
        files?.forEach(file => {
          manager.uploadFile(file);
        });
      })
      .catch(e => {
        console.error(e);
      });
  }, [manager, readonly]);

  const handleCellDoubleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      manager.selectCurrentCell(true);
    },
    [manager]
  );

  // Register this cell as a drop target
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const target: AttachmentDropTarget = {
      dropElement: el,
      highlightElement: getCellDropHighlightElement(el),
    };
    dropTargetRegistry.add(target);
    return () => {
      if (currentDropTarget === target) {
        clearDropHighlight();
      }
      dropTargetRegistry.delete(target);
    };
  }, []);

  // Listen for custom drop events dispatched by the dragging element
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onDrop = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.blobId || !activeDrag) return;
      // Validate: not same cell, same dataSource
      const sameCell =
        activeDrag.sourceRowId === manager.rowId &&
        activeDrag.sourcePropertyId === manager.propertyId;
      if (sameCell || readonly) return;
      if (activeDrag.dataSourceRef !== manager.dataSource) return;
      const moved = moveAttachment(
        manager.dataSource,
        {
          rowId: activeDrag.sourceRowId,
          propertyId: activeDrag.sourcePropertyId,
        },
        { rowId: manager.rowId, propertyId: manager.propertyId },
        activeDrag.sourceRefId,
        activeDrag.blobId
      );
      if (moved) {
        manager.copyMovedAttachmentReviewMetadata({
          sourceRowId: activeDrag.sourceRowId,
          sourcePropertyId: activeDrag.sourcePropertyId,
          sourceRefId: moved.sourceRefId,
          targetRowId: manager.rowId,
          targetPropertyId: manager.propertyId,
          targetRefId: moved.targetRefId,
          blobId: activeDrag.blobId,
        });
      }
      activeDrag = null;
    };
    el.addEventListener('attachment-pointer-drop', onDrop);
    return () => el.removeEventListener('attachment-pointer-drop', onDrop);
  }, [manager, readonly]);

  const renderPopoverContent = () => {
    if (!readonly && fileList.length === 0) {
      return (
        <div
          data-peek-view-wrapper="true"
          className={styles.uploadPopoverContainer}
        >
          <Button
            onClick={openUploadDialog}
            variant="primary"
            className={styles.uploadButton}
          >
            Choose a file
          </Button>

          <div className={styles.fileInfoContainer}>
            <div className={styles.fileSizeInfo}>
              {`The maximum size per file is ${blobLimitLabel ?? '...'}`}
            </div>
          </div>
        </div>
      );
    }
    const heicFiles = fileList.filter(isConvertibleHeic);

    return (
      <div
        data-peek-view-wrapper="true"
        className={styles.filePopoverContainer}
      >
        <div className={styles.fileListContainer}>
          {fileList.map(file => (
            <FileListItem
              key={file.type === 'done' ? file.refId : file.id}
              file={file}
              handleRemoveFile={manager.removeFile}
              manager={manager}
              readonly={readonly}
            />
          ))}
        </div>
        <div className={styles.uploadContainer}>
          {!readonly && (
            <div
              onClick={openUploadDialog}
              className={styles.uploadButtonStyle}
            >
              <PlusIcon className={styles.iconPrimary} width={20} height={20} />
              <span>Add a file or image</span>
            </div>
          )}
          {!readonly && heicFiles.length > 0 && (
            <BatchConvertButton heicFiles={heicFiles} manager={manager} />
          )}
          {fileList.filter(f => f.type === 'done').length >= 1 && (
            <DownloadAllButton fileList={fileList} manager={manager} />
          )}
        </div>
      </div>
    );
  };

  return (
    <Popover
      anchorOnly
      open={isEditing}
      onOpenChange={open => {
        manager.selectCurrentCell(open);
      }}
      contentOptions={{
        className: styles.filePopoverContent,
        onInteractOutside: event => {
          const target = event.detail.originalEvent.target;
          const cellContainer = dropRef.current?.closest(
            'dv-table-view-cell-container, affine-database-virtual-cell-container'
          );
          if (target instanceof Node && cellContainer?.contains(target)) {
            event.preventDefault();
          }
        },
      }}
      content={renderPopoverContent()}
    >
      <div
        ref={dropRef}
        style={{ overflow: 'hidden', minHeight: '100%', width: '100%' }}
        data-file-cell-drop-target={`${manager.rowId}:${manager.propertyId}`}
        onDoubleClick={handleCellDoubleClick}
      >
        <div className={styles.cellContainer}>
          {fileList.map(file => (
            <DraggableFileItem
              key={file.type === 'done' ? file.refId : file.id}
              file={file}
              readonly={readonly}
              onPointerDragStart={handlePointerDragStart}
              manager={manager}
            />
          ))}
        </div>
      </div>
    </Popover>
  );
};

/** Check if a file is convertible HEIC — by MIME or by extension fallback. */
function isConvertibleHeic(f: FileItemRenderType): f is FileItemDoneType {
  if (f.type !== 'done') return false;
  if (f.mime && isHeicMime(f.mime)) return true;
  // Fallback: check file extension (mime may not be set for older uploads)
  const ext = f.name?.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

/** sl-luoo.6: Batch-convert all HEIC files in a cell to JPEG. */
const BatchConvertButton = ({
  heicFiles,
  manager,
}: {
  heicFiles: FileItemDoneType[];
  manager: FileCellManager;
}) => {
  const [converting, setConverting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const fileUploadManager = manager.fileUploadManager;
  const count = heicFiles.length;

  const handleConvertAll = useCallback(async () => {
    setShowConfirm(false);
    setConverting(true);
    try {
      const { heicDecode } = await import('@blocksuite/affine-shared/utils');
      for (const file of heicFiles) {
        const blob = await fileUploadManager?.getFileBlob(file.id);
        if (!blob) continue;
        const { jpeg } = await heicDecode(blob);
        const jpegKey = await fileUploadManager!.storeBlob(
          jpeg,
          `${getFileDisplayNameWithoutExtension(file)}.jpg`
        );
        manager.replaceBlob(file.refId, jpegKey, 'image/jpeg', 'jpg');
      }
    } catch (err) {
      console.error('[BatchConvert] Failed:', err);
    } finally {
      setConverting(false);
    }
  }, [heicFiles, fileUploadManager, manager]);

  if (converting) {
    return (
      <div className={styles.uploadButtonStyle} style={{ opacity: 0.6 }}>
        <CircularProgress progress={0} />
        <span>Converting...</span>
      </div>
    );
  }

  if (showConfirm) {
    return (
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '4px 8px',
        }}
      >
        <span style={{ fontSize: 12 }}>
          {count === 1
            ? '1 HEIC file found. Convert to JPEG?'
            : `${count} HEIC files found. Convert all to JPEG?`}
        </span>
        <Button variant="primary" onClick={() => void handleConvertAll()}>
          Convert
        </Button>
        <Button variant="plain" onClick={() => setShowConfirm(false)}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div
      onClick={() => setShowConfirm(true)}
      className={styles.uploadButtonStyle}
    >
      <FileIcon className={styles.iconPrimary} width={20} height={20} />
      <span>Convert {count} HEIC to JPEG</span>
    </div>
  );
};

/** Download all files in a cell: 1 file → direct, 2+ files → zip. */
const DownloadAllButton = ({
  fileList,
  manager,
}: {
  fileList: FileItemRenderType[];
  manager: FileCellManager;
}) => {
  const [downloading, setDownloading] = useState(false);
  const doneCount = fileList.filter(f => f.type === 'done').length;

  const handleClick = useCallback(async () => {
    setDownloading(true);
    try {
      await downloadAllFiles(manager, fileList);
    } finally {
      setDownloading(false);
    }
  }, [manager, fileList]);

  if (downloading) {
    return (
      <div className={styles.uploadButtonStyle} style={{ opacity: 0.6 }}>
        <CircularProgress progress={0} />
        <span>Downloading...</span>
      </div>
    );
  }

  return (
    <div
      onClick={() => void handleClick()}
      className={styles.uploadButtonStyle}
    >
      <DownloadIcon className={styles.iconPrimary} width={20} height={20} />
      <span>
        {doneCount === 1 ? 'Download file' : `Download ${doneCount} files`}
      </span>
    </div>
  );
};

/** Extract short format label from MIME or filename extension. */
function formatLabel(file: FileItemRenderType): string | null {
  const mime = file.type === 'done' ? file.mime : undefined;
  if (mime) {
    const sub = mime.split('/')[1]?.toUpperCase();
    if (sub === 'JPEG') return 'JPG';
    if (sub === 'SVG+XML') return 'SVG';
    if (sub === 'VND.MICROSOFT.ICON') return 'ICO';
    if (sub) return sub;
  }
  const ext = getFileDisplayName(file).split('.').pop()?.toUpperCase();
  return ext && ext.length <= 5 ? ext : null;
}

/** Check if a file is convertible (HEIC/HEIF) */
function isConvertible(file: FileItemRenderType): file is FileItemDoneType {
  return file.type === 'done' && !!file.mime && isHeicMime(file.mime);
}

/** sl-luoo: Floating context menu for file cells with HEIC conversion option. */
export const FileListItem = (props: {
  file: FileItemRenderType;
  handleRemoveFile: (file: FileItemRenderType, e?: MouseEvent) => void;
  manager: FileCellManager;
  readonly: boolean;
}) => {
  const { file, handleRemoveFile, manager, readonly } = props;
  const { preview, fileType, onPreview } = useFilePreview(file, manager);
  const fileUploadManager = manager.fileUploadManager;
  const { openPromptModal, closePromptModal } = usePromptModal();
  const renameLabel = I18n.t('Rename');
  const emptyFileNameLabel = I18n.t('com.streamlain.blob.rename.empty');
  const displayName = getFileDisplayName(file);
  const downloadName =
    file.type === 'done' ? getFileDownloadName(file) : displayName;

  // sl-luoo.6: Client-side HEIC conversion state
  const [isConverting, setIsConverting] = useState(false);
  const canConvert = !readonly && isConvertible(file) && !isConverting;

  const handleConvertToJpeg = useCallback(
    async (e?: MouseEvent) => {
      e?.stopPropagation();
      if (!canConvert) return;
      if (!isConvertible(file)) return;

      setIsConverting(true);
      try {
        // 1. Fetch the HEIC blob from storage
        const heicBlob = await fileUploadManager?.getFileBlob(file.id);
        if (!heicBlob) {
          console.error('[Conversion] HEIC blob not found');
          return;
        }

        // 2. Decode HEIC → JPEG in browser via heic-to (WASM)
        const { heicDecode } = await import('@blocksuite/affine-shared/utils');
        const { jpeg } = await heicDecode(heicBlob);

        // 3. Upload JPEG blob to storage
        const jpegBlobKey = await fileUploadManager!.storeBlob(
          jpeg,
          `${getFileDisplayNameWithoutExtension(file)}.jpg`
        );

        // 4. Replace cell entry: HEIC → JPEG
        manager.replaceBlob(file.refId, jpegBlobKey, 'image/jpeg', 'jpg');
      } catch (error) {
        console.error('[Conversion] Client-side HEIC→JPEG failed:', error);
      } finally {
        setIsConverting(false);
      }
    },
    [canConvert, file, fileUploadManager, manager]
  );

  const handleDownloadFile = useCallback(
    async (fileId: string, e?: MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();

      try {
        const blob = await fetchFileCellBlob(manager, fileId);
        if (!blob) {
          throw new Error('Attachment blob not found');
        }

        const url = URL.createObjectURL(blob);
        downloadFile(url, downloadName);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) {
        console.error('Download failed', error);
        notify.error({ title: 'Download failed' });
      }
    },
    [downloadName, manager]
  );

  const handleRenameFile = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (readonly || file.type !== 'done') {
        return;
      }

      const { editableName, extension } = splitBlobNameForRename(displayName);
      openPromptModal(
        {
          title: renameLabel,
          label: extension ? `${renameLabel} ${extension}` : renameLabel,
          defaultValue: editableName,
          confirmText: renameLabel,
          cancelText: I18n.t('Cancel'),
          inputOptions: {
            autoSelect: true,
          },
          onConfirm: text => {
            const nextName = composeBlobNameFromRename(text, extension);
            if (!nextName) {
              notify.error({ title: emptyFileNameLabel });
              return;
            }
            manager.renameFile(file, nextName);
            closePromptModal();
          },
        },
        { autoClose: false }
      );
    },
    [
      closePromptModal,
      displayName,
      emptyFileNameLabel,
      file,
      manager,
      openPromptModal,
      readonly,
      renameLabel,
    ]
  );

  const menuItems = (
    <>
      {(fileType === 'image' ||
        fileType === 'media' ||
        fileType === 'pdf' ||
        fileType === 'text') &&
        onPreview && (
          <MenuItem
            onClick={onPreview}
            prefixIcon={<FileIcon width={20} height={20} />}
          >
            Preview
          </MenuItem>
        )}
      {!readonly && file.type === 'done' && (
        <MenuItem
          onClick={handleRenameFile}
          prefixIcon={<EditIcon width={20} height={20} />}
        >
          {renameLabel}
        </MenuItem>
      )}
      {(fileType === 'file' ||
        fileType === 'pdf' ||
        fileType === 'text' ||
        fileType === 'image' ||
        fileType === 'media') && (
        <MenuItem
          onClick={e => {
            void handleDownloadFile(file.id, e).catch(error => {
              console.error('Download failed:', error);
            });
          }}
          prefixIcon={<DownloadIcon width={20} height={20} />}
        >
          Download
        </MenuItem>
      )}
      {/* sl-luoo.6: Convert HEIC/HEIF to JPEG (client-side via heic-to) */}
      {canConvert && (
        <MenuItem
          onClick={e => {
            void handleConvertToJpeg(e).catch(error => {
              console.error('Conversion failed:', error);
            });
          }}
          prefixIcon={<FileIcon width={20} height={20} />}
        >
          Convert to JPEG
        </MenuItem>
      )}
      {!readonly && (
        <MenuItem
          onClick={e => {
            handleRemoveFile(file, e);
          }}
          type={'danger'}
          prefixIcon={<DeleteIcon width={20} height={20} />}
        >
          Delete
        </MenuItem>
      )}
    </>
  );

  return (
    <div className={styles.fileItem}>
      <div
        className={styles.fileItemContent}
        onClick={e => {
          if (!onPreview) return;
          e.stopPropagation();
          onPreview();
        }}
        style={onPreview ? { cursor: 'pointer' } : undefined}
      >
        {fileType === 'image' ? (
          <div className={styles.fileItemImagePreview}>
            {preview}
            {/* sl-luoo.6: Format badge overlay */}
            {formatLabel(file) && (
              <span className={styles.formatBadgeOverlay}>
                {formatLabel(file)}
              </span>
            )}
            {/* sl-luoo.6: Conversion progress overlay */}
            {isConverting && (
              <div className={progressIconContainer}>
                <CircularProgress progress={0} />
              </div>
            )}
          </div>
        ) : (
          <>
            {preview}
            <div className={styles.fileNameStyle}>{displayName}</div>
            {isConverting && (
              <div className={progressIconContainer}>
                <CircularProgress progress={0} />
              </div>
            )}
          </>
        )}
      </div>
      <Menu
        items={menuItems}
        rootOptions={{ modal: false }}
        contentOptions={{ className: 'sl-db-menu' }}
      >
        <div
          className={styles.menuButton}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <MoreHorizontalIcon width={16} height={16} />
        </div>
      </Menu>
    </div>
  );
};

const FileCell = forwardRef(FileCellComponent);

export const filePropertyConfig = filePropertyModelConfig.createPropertyMeta({
  icon: createIcon('FileIcon'),
  cellRenderer: {
    view: uniReactRoot.createUniComponent(FileCell),
  },
});
